import { createBaseApi } from '../baseApi';

// Create the specific API instance for orders
// Add tagTypes here if baseApi doesn't handle it centrally
export const enhancedOrderApi = createBaseApi('orderApi', ['Order', 'FulfillmentGroup', 'OrderItem']);

export const orderApi = enhancedOrderApi.injectEndpoints({
  endpoints: (builder) => ({
    // Query to get the logged-in user's orders
    getMyOrders: builder.query({
      query: ({ start = 0, limit = 10, status = 'all', orderType = 'all' } = {}) => ({
        url: '/orders/me',
        params: { _start: start, _limit: limit, status, orderType }
      }),
      providesTags: (result = [], error, arg) => [
        // Handle both old array format and new paginated format
        ...(Array.isArray(result) ? result : (result?.data || [])).map(({ id }) => ({ type: 'Order', id })),
        { type: 'Order', id: 'LIST' }, // Add a LIST tag for general list invalidation
      ],
      transformResponse: (response) => {
        // Handle both old array format and new paginated format
        if (response?.data && response?.pagination) {
          // New paginated format
          return response;
        } else if (Array.isArray(response)) {
          // Old array format - convert to paginated format for consistency
          return {
            data: response,
            pagination: {
              start: 0,
              limit: response.length,
              total: response.length,
              hasMore: false
            }
          };
        } else {
          console.error('getMyOrders received unexpected response format:', response);
          return {
            data: [],
            pagination: { start: 0, limit: 10, total: 0, hasMore: false }
          };
        }
      },
    }),

    // Mutation to cancel a specific order item - Updated to use new order-item API
    cancelOrderItem: builder.mutation({
      query: ({ orderItemId }) => ({ 
        url: `/order-items/${orderItemId}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' }, // Still invalidate orders since this affects them
        { type: 'FulfillmentGroup', id: 'LIST' }, // Also invalidate fulfillment groups
      ],
    }),
    
    // Mutation to cancel an entire fulfillment group
    cancelFulfillmentGroup: builder.mutation({
      query: ({ orderId, fulfillmentGroupId }) => ({ 
        url: `/orders/${orderId}/fulfillment-groups/${fulfillmentGroupId}/cancel`, 
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderId, fulfillmentGroupId }) => [
        { type: 'Order', id: orderId }, 
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Mutation to confirm fulfillment group delivery (customer confirms receipt)
    confirmFulfillmentGroup: builder.mutation({
      query: ({ orderId, fulfillmentGroupId }) => ({
        url: `/orders/${orderId}/fulfillment-groups/${fulfillmentGroupId}/confirm`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderId, fulfillmentGroupId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    confirmOrderItem: builder.mutation({ 
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/confirm`, 
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

    getMerchantOrders: builder.query({
      query: ({ start = 0, limit = 10, status = 'all', orderType = 'all' } = {}) => ({
        url: '/orders/merchant/me',
        params: { _start: start, _limit: limit, status, orderType }
      }),
      providesTags: (result = [], error, arg) => [
        // Handle both old array format and new paginated format
        ...(Array.isArray(result) ? result : (result?.data || [])).map(({ id }) => ({ type: 'Order', id })),
        { type: 'Order', id: 'LIST' }, // Add a LIST tag
      ],
      transformResponse: (response) => {
        // Handle both old array format and new paginated format
        if (response?.data && response?.pagination) {
          // New paginated format
          return response;
        } else if (Array.isArray(response)) {
          // Old array format - convert to paginated format for consistency
          return {
            data: response,
            pagination: {
              start: 0,
              limit: response.length,
              total: response.length,
              hasMore: false
            }
          };
        } else {
          console.error('getMerchantOrders received unexpected response format:', response);
          return {
            data: [],
            pagination: { start: 0, limit: 10, total: 0, hasMore: false }
          };
        }
      },
    }),

    getFulfillmentGroupDetails: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}?_populate=requests.returnToAddress,requests.fundCollectionPayment,returnDeliveries,orderItems,orderItems.product,orderItems.product.photos,orderItems.returnRequest,orderItems.fulfillmentSubGroup,order,order.shippingAddress,order.users_permissions_user,order.users_permissions_user.profilePic,merchant,merchant.profilePic,customer,customer.profilePic,subGroups,subGroups.orderItems,subGroups.orderItems.product`,
      transformResponse: (response) => {
        // Enhanced debugging for the payment and shipping data
        
        // Log requests data
        if (response.requests) {
        }
        
        // Log returnDeliveries data
        if (response.returnDeliveries) {
        }
        
        // Log fulfillment group shipping cost fields
        
        // Log order items to verify they're populated
        if (response.orderItems) {
        }
        
        // Log subgroups to verify they're populated
        if (response.subGroups) {
        }
        
        // Log order and shipping address to verify they're populated
        if (response.order) {
          if (response.order.shippingAddress) {
          }
        }
        
        return response;
      },
      providesTags: (result, error, fulfillmentGroupId) => {
        const tags = [
          { type: 'FulfillmentGroup', id: fulfillmentGroupId },
          result?.order?.id ? { type: 'Order', id: result.order.id } : { type: 'FulfillmentGroup', id: 'DETAIL' },
        ];
        
        // Add tags for individual order items if they exist
        if (result?.orderItems && Array.isArray(result.orderItems)) {
          result.orderItems.forEach(item => {
            tags.push({ type: 'OrderItem', id: item.id });
          });
        }
        
        // Add tags for return requests if they exist
        if (result?.requests && Array.isArray(result.requests)) {
          result.requests.forEach(req => {
            tags.push({ type: 'Request', id: req.id });
          });
        }
        
        return tags;
      }
    }),

    // NEW: Get order items by fulfillment group ID
    getOrderItemsByFulfillmentGroup: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}/order-items`,
      providesTags: (result = [], error, fulfillmentGroupId) => [
        ...result.map(({ id }) => ({ type: 'OrderItem', id })),
        { type: 'OrderItem', id: `FG_${fulfillmentGroupId}` },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Query to get order item details for returns - Updated to use new order-item API
    getOrderItemDetails: builder.query({
      query: (orderItemId) => `/order-items/${orderItemId}`,
      providesTags: (result, error, orderItemId) => [
        { type: 'OrderItem', id: orderItemId }
      ],
    }),

    // Query to get order details by ID
    getOrderDetails: builder.query({
      query: (orderId) => `/orders/${orderId}`,
      providesTags: (result, error, orderId) => [
        { type: 'Order', id: orderId }
      ],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Mutation to initiate an item return - Updated to use new order-item API
    initiateItemReturn: builder.mutation({
      query: ({ fulfillmentGroupId, orderItemId, reason, quantity, comments }) => ({
        url: `/order-items/${orderItemId}/initiate-return`,
        method: 'POST',
        body: { reason, quantity, comments, fulfillmentGroupId }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId, orderItemId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // NEW MUTATION for confirming label creation and invalidating cache
    confirmLabelCreation: builder.mutation({
      // This mutation is called by the frontend AFTER the backend has successfully
      // created the label and updated the Delivery & FulfillmentGroup records.
      // Its primary purpose here is to tell RTK Query to refetch order lists.
      // It could optionally send some data or hit a minimal backend confirmation endpoint.
      query: ({ orderId, fulfillmentGroupId }) => ({
        url: `/orders/${orderId}/fulfillment-groups/${fulfillmentGroupId}/label-confirmed`, // Example endpoint, might not be strictly needed if backend is already updated
        method: 'POST', // Or PUT
        // body: { status: 'label_creation_acknowledged_by_frontend' } // Optional body
      }),
      // This is the crucial part for auto-refresh
      invalidatesTags: (result, error, { orderId, fulfillmentGroupId }) => [
        { type: 'Order', id: 'LIST' }, // Invalidates getMyOrders and getMerchantOrders lists
        orderId ? { type: 'Order', id: orderId } : undefined, // Invalidates specific order if ID known
        fulfillmentGroupId ? { type: 'FulfillmentGroup', id: fulfillmentGroupId } : undefined, // Invalidates specific FG details
        { type: 'OrderItem', id: 'LIST' }, // Invalidate all order items
      ].filter(Boolean), // Remove undefined entries
    }),

    // NEW MUTATION for creating an order
    createOrder: builder.mutation({
      query: (preparedOrderData) => ({
        url: '/orders', // Your backend endpoint for creating orders
        method: 'POST',
        body: preparedOrderData,
        // Headers like Authorization will be handled by your baseQuery setup in createBaseApi
      }),
      // This will auto-refresh queries that provide the 'Order' LIST tag (e.g., getMyOrders, getMerchantOrders)
      invalidatesTags: (result, error, arg) => [
        { type: 'Order', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' }, // Also invalidate order items
      ],
    }),

    // NEW MUTATIONS for return management
    
    // Cancel return request (customer action)
    cancelReturnRequest: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/cancel-return`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderItemId, fulfillmentGroupId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        fulfillmentGroupId ? { type: 'FulfillmentGroup', id: fulfillmentGroupId } : undefined,
        { type: 'Order', id: 'LIST' },
      ].filter(Boolean),
    }),

    // Start return shipping (customer action after approval)
    startReturnShipping: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/start-return`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderItemId, fulfillmentGroupId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        fulfillmentGroupId ? { type: 'FulfillmentGroup', id: fulfillmentGroupId } : undefined,
        { type: 'Order', id: 'LIST' },
      ].filter(Boolean),
    }),

    // Request group return (customer action)
    requestGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, reason, comments }) => ({
        url: `/requests/group-return`,
        method: 'POST',
        body: { fulfillmentGroupId, reason, comments }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Cancel group return (customer action)
    cancelGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/cancel-group-return`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Approve group return (merchant action) 
    approveGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, approvalComments }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/approve-group-return`,
        method: 'PUT', 
        body: { approvalComments }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Reject group return (merchant action)
    rejectGroupReturn: builder.mutation({
      query: ({ fulfillmentGroupId, rejectionReason }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/reject-group-return`,
        method: 'PUT',
        body: { rejectionReason }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

  }),
  overrideExisting: false, 
});

export const { 
  useGetMyOrdersQuery, 
  useGetMerchantOrdersQuery,
  useCancelOrderItemMutation,
  useCancelFulfillmentGroupMutation,
  useConfirmFulfillmentGroupMutation,
  useConfirmOrderItemMutation,
  useGetFulfillmentGroupDetailsQuery,
  useConfirmLabelCreationMutation,
  useCreateOrderMutation,
  useGetOrderItemDetailsQuery,
  useGetOrderDetailsQuery,
  useInitiateItemReturnMutation,
  useGetOrderItemsByFulfillmentGroupQuery, // Export the new query hook
  // NEW RETURN MANAGEMENT HOOKS
  useCancelReturnRequestMutation,
  useStartReturnShippingMutation,
  useRequestGroupReturnMutation,
  useCancelGroupReturnMutation,
  useApproveGroupReturnMutation,
  useRejectGroupReturnMutation,
} = orderApi;
